// overlayStore — a reactive store whose BASE is a read-only remote corpus and whose WRITES are a
// local overlay. Same surface as `createReactiveLocalStore`, so spine consumers are unchanged.
//
// WHY (PB-04 Inc 2). The 350-well prebake writes ~28.7 MB into `oxbo-wells-v1` against a ~5 MB
// origin quota. `oxbo-pads-v1` is small and persists; the wells do not, so on RELOAD the user has
// pads and no wells, and the quota that did get consumed then breaks the next authored write — an
// AFE save failed exactly this way (see BACKLOG PB-01). Serving the corpus from the API means the
// browser holds an index instead of the whole aggregate: 590 bytes per well instead of 631 KB
// (`python3 scripts/measure_corpus_api.py --only index`).
//
// AUTHORING MUST SURVIVE. A read-only remote store alone would break well/pad creation, so this is
// an OVERLAY, not a replacement: the corpus is the base, and everything the user creates or edits
// is persisted locally under a SEPARATE key. Authored records keep the full ~5 MB, which was the
// point.
//
// NON-DESTRUCTIVE (Charter Abs-7). The overlay lives at `<key>:overlay`, never at `<key>`. Turning
// corpus mode off restores the previous local spine byte-for-byte — nothing is migrated, nothing is
// overwritten, and the two can coexist on one origin.
//
// Charter Abs-2: pure state container — NO engineering / economic / statistical math.

type Listener = () => void;

/** The minimum an overlaid record must have. Both `Pad` and `Well` satisfy it. */
export interface Identified {
  id: string;
}

/** What the overlay persists: local records plus the ids of base records removed locally. */
interface OverlayData<T> {
  records: T[];
  /** Base ids deleted locally. Without these a delete would silently reappear on reload. */
  tombstones: string[];
  /** Ids in `records` the USER actually authored, as opposed to corpus-derived records that
   *  leaked in through a whole-array write.
   *
   *  THIS IS THE DISTINCTION THE STORE COULD NOT MAKE, and both of 2026-09-08's defects are it.
   *  Without the mark, precedence has to be one blanket rule and either arm loses data: overlay-
   *  first let a leaked corpus record permanently mask a well's real jobs (the five-day wrong-well
   *  defect); transient-first then made the user's own edit invisible and destroyed it on the next
   *  write. Marked, each record gets the rule it needs — authored beats hydration, leakage does not.
   *
   *  OPTIONAL, and absent means "not authored". An overlay written before this existed is treated
   *  as leakage, which is the safe reading: it un-masks the corpus and the user's real edits are
   *  re-marked the next time they save. */
  authored?: string[];
  /** When each authored record was written (`Date.now()`-seeded monotonic). Precedence between an
   *  authored record and a hydrate is MOST-RECENT-WRITER, not a fixed layer order, because every
   *  hydrator in this app is a read-modify-write against `read()` — so a hydrate installed after an
   *  edit already CONTAINS that edit and is strictly newer. A fixed order gets one case wrong
   *  whichever way it points: overlay-always loses the corpus detail a hydrate just merged in,
   *  transient-always loses the edit (the 2026-09-08 Sev-0). */
  authoredAt?: Record<string, number>;
}

export interface OverlayStore<T extends Identified> {
  /** The localStorage key the OVERLAY persists to (`<baseKey>:overlay`). */
  readonly key: string;
  /** Merged view: base with local overrides applied, local-only records appended. STABLE
   *  reference while neither the base snapshot nor the overlay string has changed — required by
   *  `useSyncExternalStore`, which loops on a fresh array every call. */
  read: () => T[];
  /** Replace the whole merged array. Only what DIFFERS from the base is persisted. */
  setAll: (next: T[]) => void;
  subscribe: (listener: Listener) => () => void;
  /** Fetch the base once. Idempotent; safe to call from many components. */
  hydrate: () => Promise<void>;
  /** Install SESSION-ONLY records that override the base and are NEVER persisted.
   *  Used to hydrate a well's full detail (jobs, reports, surveys) when the user opens it:
   *  that payload is ~1 MB, so persisting it would rebuild the very quota failure this store
   *  exists to prevent — eight wells browsed would exceed the ~5 MB origin cap. */
  setTransient: (records: T[]) => void;
  /** How many transient records are resident (the LRU is capped). */
  transientCount: () => number;
  /** Is this record's hydrated detail still resident? Callers that dedupe hydration MUST ask
   *  the store rather than keeping their own set — the LRU evicts underneath them. */
  hasTransient: (id: string) => boolean;
  /** Does the read-only corpus serve this record?
   *
   *  DISAMBIGUATES AN EMPTY COLLECTION. The thin index strips a well's `jobs` by design, so an
   *  un-opened corpus well and a genuinely job-less well both present `jobs: []`. A caller that
   *  reads that as "no jobs here" mints a duplicate — which is how three wells in the PO's
   *  storage ended up carrying a second, misnamed frac job (2026-09-08). `hasBase(id) &&
   *  !hasTransient(id)` is the precise statement of "the corpus has this record and its detail is
   *  not loaded", which is the only case where an empty collection means nothing at all.
   *
   *  `false` for a locally-created record, which is correct: nothing else holds its data. */
  hasBase: (id: string) => boolean;
  /** Hydration state, for a UI that wants to say "loading the corpus". */
  status: () => "idle" | "loading" | "ready" | "error";
  /** Last hydration error, if any. */
  error: () => string | null;
}

const overlayKeyFor = (baseKey: string): string => `${baseKey}:overlay`;

/** How many fully-hydrated records stay resident.
 *
 *  MEASURED over all 2,538 wells by `python3 scripts/measure_corpus_api.py --only bundles`:
 *  p50 47.6 KB · p90 192.4 KB · max 1.03 MB. So 25 resident records is ~1.2 MB typical.
 *
 *  CORRECTED 2026-08-28: this previously claimed "126 KB at p50 … roughly 3 MB typical" — one of
 *  four Sev-1 measurement defects, and MISSED by the first correction pass even though that pass
 *  claimed to have fixed every site. Two independent reviewers found it with one grep. */
const TRANSIENT_MAX = 25;

/** Above this many base records vanishing in ONE `setAll` — and more than half the base — the
 *  write is treated as an accident and the deletions are refused. See the guard in `setAll`. */
const MASS_DELETE_LIMIT = 50;

/** Content equality that FAILS CLOSED. A cyclic or otherwise non-serializable record must be
 *  treated as changed (and persisted) rather than throwing out of `setAll` into the UI. */
function safeEqual(a: unknown, b: unknown): boolean {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

/** Sentinel memo key used while the latch is set, so it can never collide with a real value. */
const UNPERSISTED = "\u0000unpersisted";

/** `getItem` that cannot throw — the one unguarded call here previously propagated a
 *  `SecurityError` out of `setAll` into the UI when storage is disabled (specialist review). */
function readOverlayRaw(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function readOverlay<T>(key: string): {
  raw: string | null;
  data: OverlayData<T>;
} {
  let raw: string | null;
  try {
    raw = localStorage.getItem(key);
  } catch {
    return { raw: null, data: { records: [], tombstones: [] } };
  }
  if (!raw) return { raw, data: { records: [], tombstones: [] } };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === "object" &&
      Array.isArray((parsed as OverlayData<T>).records)
    ) {
      const d = parsed as OverlayData<T>;
      return {
        raw,
        data: {
          records: d.records,
          tombstones: Array.isArray(d.tombstones) ? d.tombstones : [],
          ...(Array.isArray(d.authored) ? { authored: d.authored } : {}),
          ...(d.authoredAt && typeof d.authoredAt === "object"
            ? { authoredAt: d.authoredAt }
            : {}),
        },
      };
    }
  } catch {
    /* corrupt → behave as empty rather than throwing on every read */
  }
  return { raw, data: { records: [], tombstones: [] } };
}

/**
 * @param baseKey  the spine key this overlays (`oxbo-wells-v1`); the overlay is `<baseKey>:overlay`
 * @param fetchBase  loads the read-only corpus (one call, the thin index)
 */
export function createOverlayStore<T extends Identified>(
  baseKey: string,
  fetchBase: () => Promise<T[]>,
): OverlayStore<T> {
  const key = overlayKeyFor(baseKey);
  const listeners = new Set<Listener>();

  let base: T[] = [];
  // Transient detail layer: base < transient < persisted overlay. Insertion-ordered Map doubles
  // as the LRU — re-setting a record moves it to the end, and eviction takes from the front.
  const transient = new Map<string, T>();
  let transientVersion = 0;
  /** When each transient record was installed, on the same clock as `authoredAt`. */
  const transientAt = new Map<string, number>();
  /** Monotonic and comparable across a reload: seeded from the wall clock, then only ever
   *  incremented, so two writes in the same millisecond still order. */
  let seq = Date.now();
  const nextSeq = (): number => (seq += 1);
  let status: "idle" | "loading" | "ready" | "error" = "idle";
  let error: string | null = null;
  let inflight: Promise<void> | null = null;

  // Memoized merge. `read()` is called on every render by every subscriber, so it must not
  // re-merge 2,538 records each time — and must not return a fresh array, which would make
  // useSyncExternalStore loop forever.
  // When a write cannot persist (quota exceeded / private mode), the in-memory payload is
  // AUTHORITATIVE for the session — `reactiveLocalStore` and `jobTimeLogStore` both do this and
  // this store shipped without it (Sev-2, specialist review 2026-08-28). Without the latch,
  // `read()` reconciles against the STALE storage string on the next memo miss, so opening
  // another well silently reverted the user's unsaved edit with no error shown. The overlay is
  // the user's own work, so losing it is the expensive failure.
  let unpersisted: OverlayData<T> | null = null;
  let memoBase: T[] | null = null;
  let memoRaw: string | null | undefined;
  let memoTransient = -1;
  let memoValue: T[] = [];

  const notify = (): void => listeners.forEach((l) => l());

  const merge = (overlay: OverlayData<T>): T[] => {
    const overrideById = new Map(overlay.records.map((r) => [r.id, r]));
    const authored = new Set(overlay.authored ?? []);
    const authoredAt = overlay.authoredAt ?? {};
    const tomb = new Set(overlay.tombstones);
    const out: T[] = [];
    const baseIds = new Set<string>();
    // Base order is preserved and overrides are spliced IN PLACE, so editing a well does not
    // make it jump to the end of every list showing it.
    //
    // PRECEDENCE, FOR A RECORD THE CORPUS ALSO SERVES: hydrated detail > overlay > base.
    //
    // This inverts the first two, and the inversion is the fix. The old order put the persisted
    // overlay first on the reasoning that "the user's own edit must never be masked by a freshly
    // hydrated corpus record" — sound for a record the user authored, and wrong for the far more
    // common case, because ANY write that round-trips `read()` can leave a corpus-derived record
    // in the overlay, and that record then masked the corpus PERMANENTLY with no way to clear it.
    // Measured on the PO's own storage 2026-09-08: `oxbo-wells-v1:overlay` held PH-2H, PH-3H and
    // PH-4H, all stamped with the corpus's own `createdAt` (2026-09-03T00:00:00.000Z), each
    // carrying one job misnamed for a different well, each hiding that well's real drilling and
    // completion jobs. Nothing in that overlay was authored.
    //
    // It also makes the code obey a ruling already on the books — docs/DECISIONS.md 2026-09-03,
    // "Local/localStorage mode is RETIRED": localStorage holds only the user's own edits, never
    // the corpus. An overlay record for a corpus well is that second path, and giving it absolute
    // precedence is what made the leak permanent instead of merely wasteful.
    //
    // A REAL EDIT IS STILL SAFE, for a structural reason rather than by luck: every hydrator is a
    // read-modify-write against `read()` — `useCorpusWellDetail.hydratePreMappedBundle` and its
    // mapping path both spread the existing (overlay-inclusive) record before calling
    // `setTransient` — so the transient record already CARRIES the overlay's fields. That is load
    // bearing: a hydrator that stopped merging the current record would drop authored fields here.
    //
    // A LOCALLY-CREATED record is untouched by this. It has no base row, so it is emitted by the
    // overlay-only loop below and keeps absolute precedence.
    for (const r of base) {
      baseIds.add(r.id);
      if (tomb.has(r.id)) continue;
      const o = overrideById.get(r.id);
      const t = transient.get(r.id);
      // AN UNAUTHORED OVERLAY RECORD IS NEVER READ — hydrated or not.
      //
      // If the user did not author it, it is leakage: a corpus record that reached localStorage
      // through a whole-array write. The corpus row beside it is better data by definition. An
      // earlier fix only let a HYDRATE outrank it, which left it serving whenever the well was not
      // open — and that is what still put "Completion - PH-1H" on PH-3H and PH-4H in the zipper,
      // because a sibling the user has not opened has no hydrate to outrank anything. Ignoring it
      // costs nothing, and `setAll` then drops it on the next write, so storage cleans itself and
      // the user never clears anything by hand. That last part is the requirement: the PO's words
      // are on record that he must never have to.
      const usable = o !== undefined && authored.has(r.id) ? o : undefined;
      const wins =
        usable !== undefined &&
        (t === undefined ||
          (transientAt.get(r.id) ?? 0) < (authoredAt[r.id] ?? 0));
      out.push(wins ? usable : (t ?? usable ?? r));
    }
    // A record with NO base row is emitted regardless of the mark: nothing else holds it, so
    // ignoring it would be the data loss this rule exists to prevent. Leakage is only possible for
    // ids the corpus also serves.
    for (const r of overlay.records) {
      if (!baseIds.has(r.id)) out.push(r);
    }
    // A transient record whose id is NOT in the base would otherwise be invisible — held in the
    // map, never rendered. That is a silent-drop footgun, so it is appended like an overlay-only
    // record. (Caught by the LRU test, which installed transient ids with no base counterpart.)
    const emitted = new Set(out.map((r) => r.id));
    for (const [id, r] of transient) {
      if (!emitted.has(id) && !tomb.has(id)) out.push(r);
    }
    return out;
  };

  const read = (): T[] => {
    if (unpersisted !== null) {
      // Do not reconcile against storage: it holds the stale pre-failure value.
      if (
        memoBase === base &&
        memoTransient === transientVersion &&
        memoRaw === UNPERSISTED
      ) {
        return memoValue;
      }
      memoBase = base;
      memoRaw = UNPERSISTED;
      memoTransient = transientVersion;
      memoValue = merge(unpersisted);
      return memoValue;
    }
    const { raw, data } = readOverlay<T>(key);
    if (
      memoBase === base &&
      memoRaw === raw &&
      memoTransient === transientVersion
    ) {
      return memoValue;
    }
    memoBase = base;
    memoRaw = raw;
    memoTransient = transientVersion;
    memoValue = merge(data);
    return memoValue;
  };

  const setTransient = (records: T[]): void => {
    for (const r of records) {
      transient.delete(r.id); // re-insert at the end: most recently used
      transient.set(r.id, r);
      transientAt.set(r.id, nextSeq());
    }
    // Uncapped, a long browse walks the tab into a memory ceiling instead of a storage one —
    // a slower version of the same bug.
    while (transient.size > TRANSIENT_MAX) {
      const oldest = transient.keys().next();
      if (oldest.done) break;
      transientAt.delete(oldest.value);
      transient.delete(oldest.value);
    }
    transientVersion += 1;
    notify();
  };

  const setAll = (next: T[]): void => {
    const baseById = new Map(base.map((r) => [r.id, r]));
    const records: T[] = [];
    // Authored edits to a RESIDENT record, which must replace the stale hydrate they were built
    // from. Collected and applied together after the loop so the LRU is touched once.
    const refreshed: T[] = [];
    // ALREADY-AUTHORED RECORDS MUST SURVIVE A WRITE THAT DOES NOT TOUCH THEM (Sev-0, 2026-09-08).
    // `setAll` rebuilds the payload from scratch and then REPLACES the whole overlay, so a record
    // that is unchanged since its last save is dropped unless it is carried forward — measured as
    // "editing PH-3H deletes PH-2H's day report". Prior state is read once, here.
    const prior = readOverlay<T>(key).data;
    const priorAuthored = new Set(prior.authored ?? []);
    const priorById = new Map(prior.records.map((r) => [r.id, r]));
    const authoredOut = new Set<string>();
    const authoredAtOut: Record<string, number> = {
      ...(prior.authoredAt ?? {}),
    };
    const seen = new Set<string>();
    for (const r of next) {
      seen.add(r.id);
      const b = baseById.get(r.id);
      const t = transient.get(r.id);

      // A HYDRATED RECORD IS NEVER "LOCALLY CREATED", AND THE ORDER OF THESE TWO CHECKS IS THE
      // WHOLE POINT (Sev-0, specialist review 2026-09-08).
      //
      // `merge` deliberately emits transient records whose id is NOT in the base, so a well
      // hydrated before the index lands is visible rather than silently dropped. `setAll` then saw
      // `b === undefined` and classified every one of them as locally created — writing the full
      // ~880 KB bundle to localStorage. Measured: an ordinary edit to ONE well persisted 25
      // transient records; and with `hydrate()` failing (base = [], see the catch in `hydrate`)
      // a plain well-create persisted every hydrated well resident at the time. That is the exact
      // quota rebuild this store exists to prevent, reachable whenever the compute server is slow.
      //
      // Testing residency FIRST means a record the corpus supplied is measured against what the
      // corpus supplied, whether or not its index row has arrived.
      if (t !== undefined) {
        // HYDRATED-DETAIL GUARD (Sev-1, adversarial review 2026-08-28). This was
        // `transient.get(r.id) === r` — reference identity — which holds only if the caller hands
        // back the exact object `read()` produced. Every real consumer clones:
        // `useWellData.updateWell`/`archiveWell` do `read().map(w => w.id === id ? {...w, …} : w)`.
        // The spread broke identity, so control fell through to the stringify below, which
        // compared the ~590-byte thin index row against the fully hydrated record, found them
        // different, and wrote the ENTIRE detail bundle to the overlay — one archive click
        // measured 200 KB.
        //
        // Comparing against the TRANSIENT record instead means an edit to a hydrated well persists
        // only for wells the user actually changed, never for one merely browsed.
        if (t === r || safeEqual(t, r)) {
          // Unchanged against what is resident. If it was authored earlier it stays authored and
          // stays persisted; if it was merely browsed, it is still not overlay material.
          const kept = priorById.get(r.id);
          if (kept !== undefined && priorAuthored.has(r.id)) {
            records.push(kept);
            authoredOut.add(r.id);
          }
          continue;
        }
        records.push(r);
        authoredOut.add(r.id);
        authoredAtOut[r.id] = nextSeq();
        // THE EDIT MUST ALSO BECOME THE RESIDENT RECORD (Sev-0, specialist review 2026-09-08).
        //
        // Persisting alone was a data-loss bug for the whole of 2026-09-08. When hydrated detail
        // began outranking the overlay earlier that day, `setAll` still wrote only to the overlay
        // and left `transient` holding the PRE-EDIT hydrate — so the user's own change was
        // invisible the instant they made it, and the loss compounded: the next `setAll` maps over
        // a `read()` that still hands out the stale transient object, hits the `t === r` fast path
        // above, omits this record from `records`, and the whole-overlay write then DROPS the
        // authored content from storage. Measured: renaming a well returned the old name from
        // `read()`; editing a second well left the first well's day report nowhere at all.
        //
        // Refreshing both layers is what makes them agree instead of race. It preserves the reason
        // hydration outranks the overlay — a corpus-derived record must never mask fresh corpus
        // detail — because what wins here is the record the user just authored, not a stale one.
        refreshed.push(r);
        continue;
      }

      if (b === undefined) {
        records.push(r); // locally created — no corpus row and no hydrated detail
        authoredOut.add(r.id);
        authoredAtOut[r.id] = nextSeq();
        continue;
      }
      if (b === r) continue; // untouched base record — read() handed out this very object

      // Reference differs but content may not: the spine's facade clones records wholesale on
      // upsert, and persisting an identical copy of every corpus record is exactly the quota
      // blow-up this store exists to prevent.
      if (!safeEqual(b, r)) {
        records.push(r);
        authoredOut.add(r.id);
        authoredAtOut[r.id] = nextSeq();
      } else if (priorById.has(r.id) && priorAuthored.has(r.id)) {
        // Identical to base now, but the user authored it before — keep the record and the mark
        // rather than silently discarding their history of having touched it.
        records.push(priorById.get(r.id)!);
        authoredOut.add(r.id);
      }
    }
    // MASS-DELETE GUARD (Sev-1, adversarial review 2026-08-28). A tombstone is INFERRED from a
    // base record's absence, so any `setAll` with a subset silently deletes the complement — and
    // that is reachable from a visible button: `useDemoSeed.reloadDemoData()` calls
    // `wellsStore.setAll([])`, which tombstoned all 2,538 corpus wells into the overlay,
    // PERSISTENTLY, with no UI able to undo it. Deleting most of the corpus is never something a
    // caller means implicitly; a real delete goes one or a few at a time.
    const removed = base.filter((r) => !seen.has(r.id));
    const priorTombstones = prior.tombstones;
    const massDelete =
      removed.length > MASS_DELETE_LIMIT && removed.length > base.length / 2;
    if (massDelete) {
      // eslint-disable-next-line no-console
      console.error(
        `overlayStore(${key}): refused an implicit mass delete of ${removed.length} of ` +
          `${base.length} read-only corpus records. Existing tombstones preserved.`,
      );
    }
    const tombstones = massDelete ? priorTombstones : removed.map((r) => r.id);

    const payload: OverlayData<T> = {
      records,
      tombstones,
      authored: [...authoredOut],
      authoredAt: Object.fromEntries(
        [...authoredOut].map((id) => [id, authoredAtOut[id] ?? nextSeq()]),
      ),
    };
    let persisted = false;
    try {
      localStorage.setItem(key, JSON.stringify(payload));
      persisted = true;
    } catch {
      // Quota / private mode. The overlay is the user's OWN work, so this is a real loss — but
      // throwing here would break the UI mid-edit. Latch instead: the payload stays authoritative
      // for the session and `read()` stops consulting the stale stored value.
      // eslint-disable-next-line no-console
      console.error(
        `overlayStore(${key}): could not persist — your changes are held for this session only.`,
      );
    }
    // Re-seat the edited records at the head of the LRU before the memo is rebuilt below; the
    // delete-then-set is `setTransient`'s own idiom (re-insert marks most-recently-used).
    for (const r of refreshed) {
      if (!transient.has(r.id)) continue; // evicted mid-write — the overlay alone now carries it
      transient.delete(r.id);
      transient.set(r.id, r);
    }
    if (refreshed.length > 0) transientVersion += 1;

    unpersisted = persisted ? null : payload;
    // Adopt the merged result directly so subscribers see exactly `next`'s effect without
    // waiting on a storage round-trip.
    memoBase = base;
    memoRaw = persisted ? readOverlayRaw(key) : UNPERSISTED;
    memoTransient = transientVersion;
    memoValue = merge(payload);
    notify();
  };

  const hydrate = async (): Promise<void> => {
    if (status === "ready" || inflight) return inflight ?? Promise.resolve();
    status = "loading";
    error = null;
    inflight = (async () => {
      try {
        base = await fetchBase();
        status = "ready";
      } catch (e) {
        error = e instanceof Error ? e.message : String(e);
        status = "error";
        base = []; // the overlay alone still renders — degraded, not broken
      } finally {
        inflight = null;
        memoBase = null; // invalidate: the base changed
        notify();
      }
    })();
    return inflight;
  };

  const subscribe = (listener: Listener): (() => void) => {
    listeners.add(listener);
    // Lazy hydration on first subscriber: no component has to know this store is remote.
    void hydrate();
    const onStorage = (e: StorageEvent) => {
      if (e.key === key || e.key === null) listener();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      listeners.delete(listener);
      window.removeEventListener("storage", onStorage);
    };
  };

  return {
    key,
    read,
    setAll,
    subscribe,
    setTransient,
    transientCount: () => transient.size,
    hasTransient: (id: string) => transient.has(id),
    hasBase: (id: string) => base.some((r) => r.id === id),
    hydrate,
    status: () => status,
    error: () => error,
  };
}
